import React from 'react';

const cardStyle = {
  backgroundColor: 'var(--accent-color)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  padding: '1.25rem',
  marginBottom: '1.25rem',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
};

const listStyle = {
  listStyleType: 'disc',
  paddingLeft: '1.5rem',
  marginTop: '0.5rem',
  lineHeight: '1.65',
  color: 'var(--secondary-color)',
};

const linkStyle = {
  color: '#0d4a75',
  textDecoration: 'underline',
  textDecorationThickness: '2px',
  textUnderlineOffset: '3px',
  fontWeight: 700,
  display: 'inline',
};

const pillStyle = {
  display: 'inline-block',
  marginRight: '0.5rem',
  marginBottom: '0.5rem',
  padding: '0.28rem 0.55rem',
  borderRadius: '999px',
  backgroundColor: '#eaf4fb',
  color: '#0d4a75',
  fontSize: '0.82rem',
  fontWeight: 700,
};

const videos = [
  {
    title: 'Informational Podcast - Episode 1',
    description:
      'Navigating your career path, understanding early career decisions, and setting a strong foundation for long-term professional growth.',
    url: 'https://myresumebuilder.blob.core.windows.net/ceapvideos/1%20Navigating_Your_Career_C.mp4',
  },
  {
    title: 'Informational Podcast - Episode 2: Organization & Planning',
    description:
      'Practical organization strategies for applications, deadlines, documents, and follow-ups.',
    url: 'https://myresumebuilder.blob.core.windows.net/ceapvideos/2%20Your_Guide_to_the_Job_Search%20B.mp4',
  },
  {
    title: 'Informational Podcast - Episode 3: Networking',
    description:
      'Relationship-building strategies that help uncover opportunities and strengthen a job search.',
    url: 'https://myresumebuilder.blob.core.windows.net/ceapvideos/3%20Your_Job_Search,_Systematized%20A.mp4',
  },
  {
    title: 'Informational Podcast - Episode 4: Federal Job Search',
    description:
      'USAJOBS navigation, vacancy announcements, required documents, hiring timelines, and competitive federal applications.',
    url: 'https://myresumebuilder.blob.core.windows.net/ceapvideos/4%20Cracking_Federal_Apps%20B.mp4',
  },
  {
    title: 'Informational Podcast - Episode 5: Cracking Federal Jobs',
    description:
      'Qualification requirements, specialized experience, federal resume tailoring, and referral strategy.',
    url: 'https://myresumebuilder.blob.core.windows.net/ceapvideos/5%20Cracking_Federal_Jobs%20A.mp4',
  },
  {
    title: 'Veterans Transition Guide',
    description:
      'Guidance for veterans translating military experience into civilian and federal career language.',
    url: 'https://myresumebuilder.blob.core.windows.net/ceapvideos/Old%20Veterans%20Video%20A.mp4',
  },
];

const agencyLinks = [
  ['USAJOBS - Official Federal Job Portal', 'https://www.usajobs.gov/'],
  ['Department of State Careers', 'https://www.state.gov/careers/'],
  ['CIA Careers', 'https://www.cia.gov/careers/'],
  ['FBI Jobs', 'https://www.fbijobs.gov/'],
  ['NSA Careers', 'https://www.nsa.gov/careers/'],
  ['Intelligence Community Careers', 'https://www.intelligencecareers.gov/'],
  ['DHS Careers', 'https://www.dhs.gov/careers'],
  ['DoD Civilian Careers', 'https://www.dodciviliancareers.com/'],
  ['VA Careers', 'https://www.va.gov/jobs/'],
  ['Treasury Careers', 'https://home.treasury.gov/about/careers-at-treasury'],
  ['DOJ Careers', 'https://www.justice.gov/careers'],
  ['Energy Department Careers', 'https://www.energy.gov/jobs'],
  ['NASA Careers', 'https://www.nasa.gov/careers/'],
  ['CDC Jobs', 'https://www.cdc.gov/jobs/'],
  ['USPTO Jobs', 'https://www.uspto.gov/jobs'],
  ['SEC Careers', 'https://www.sec.gov/careers'],
  ['EPA Careers', 'https://www.epa.gov/careers'],
  ['Transportation Careers', 'https://www.transportation.gov/careers'],
  ['Commerce Careers', 'https://www.commerce.gov/work-with-us/careers'],
  ['USGS Careers', 'https://www.usgs.gov/human-capital'],
];

const videoCard = {
  ...cardStyle,
  minWidth: '320px',
  maxWidth: '360px',
  flex: '0 0 auto',
  scrollSnapAlign: 'start',
};

const Section = ({ title, children, open = false }) => (
  <details open={open} style={{ marginBottom: '1.25rem' }}>
    <summary
      style={{
        color: 'var(--primary-color)',
        cursor: 'pointer',
        fontSize: '1.35rem',
        fontWeight: 700,
        marginBottom: '0.75rem',
      }}
    >
      {title}
    </summary>
    <div style={cardStyle}>{children}</div>
  </details>
);

const ExternalLink = ({ href, children, style = linkStyle }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" style={style}>
    {children}
  </a>
);

const InfoScreen = () => {
  return (
    <div
      className="info-screen"
      style={{
        padding: '2rem',
        maxWidth: '980px',
        margin: '0 auto',
        lineHeight: '1.6',
        color: 'var(--secondary-color)',
      }}
    >
      <header style={{ marginBottom: '1.5rem' }}>
        <p
          style={{
            color: '#1f6f8b',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: '0.35rem',
          }}
        >
          Federal career resources
        </p>
        <h1
          style={{
            color: 'var(--primary-color)',
            fontSize: '2.15rem',
            margin: 0,
          }}
        >
          Federal Job Search Tips & Strategies
        </h1>
        <p style={{ color: 'var(--muted-color)', marginTop: '0.75rem' }}>
          Use this page as a federal-first checklist for USAJOBS applications,
          federal resumes, documents, agency research, veterans transition,
          Pathways opportunities, and interview preparation.
        </p>
      </header>

      <section style={cardStyle}>
        <h2 style={{ color: 'var(--primary-color)', marginTop: 0 }}>
          Current Federal Application Priorities
        </h2>
        <p>
          USAJOBS guidance now emphasizes concise, announcement-specific resumes.
          Build every application from the vacancy announcement instead of using
          one generic resume.
        </p>
        <div style={{ marginTop: '0.75rem' }}>
          <span style={pillStyle}>Two-page federal resume limit on USAJOBS</span>
          <span style={pillStyle}>Match every required qualification</span>
          <span style={pillStyle}>Resume must support questionnaire answers</span>
          <span style={pillStyle}>Required documents control eligibility</span>
        </div>
        <ul style={listStyle}>
          <li>
            Read the entire announcement before writing: Duties, Requirements,
            Qualifications, How You Will Be Evaluated, How to Apply, and Required Documents.
          </li>
          <li>
            For each work entry, include employer, job title, month/year dates,
            hours per week, and short results-focused descriptions tied to the posting.
          </li>
          <li>
            If education is used to qualify, include the required degree,
            coursework, completion date, GPA when relevant, and transcripts when requested.
          </li>
          <li>
            Do not include sensitive items such as SSN, photos, classified
            information, or personal demographic details.
          </li>
        </ul>
      </section>

      <Section title="Federal Application Workflow" open>
        <ol style={{ ...listStyle, listStyleType: 'decimal' }}>
          <li>
            Create or update your USAJOBS profile, resume, contact information,
            work history, education, and documents before a deadline is close.
          </li>
          <li>
            Save searches for job series, grade levels, locations, remote/telework
            filters, student/recent graduate paths, and agencies you want to follow.
          </li>
          <li>
            Check eligibility first in This Job Is Open To. Do not spend time on
            an application unless you are in one of the listed hiring paths.
          </li>
          <li>
            Check qualifications second. Your resume must clearly show the
            required specialized experience, education, license, certification,
            or selective placement factor.
          </li>
          <li>
            Preview the questionnaire when available. Your resume must support
            the level of experience you claim in questionnaire answers.
          </li>
          <li>
            Upload every required document before submitting. Missing required
            documents can stop an otherwise strong application.
          </li>
        </ol>
      </Section>

      <Section title="Federal Resume Checklist" open>
        <ul style={listStyle}>
          <li>
            Keep the USAJOBS resume to two pages unless the announcement explicitly
            requests another format, such as a CV or long-form resume.
          </li>
          <li>
            Use the announcement's language for required skills and specialized
            experience, but only when the profile evidence supports it.
          </li>
          <li>
            Start bullets with concrete outcomes: improved, resolved, reviewed,
            configured, analyzed, coordinated, documented, trained, or protected.
          </li>
          <li>
            Add numbers where true: volume, dollar value, caseload, users served,
            reports completed, response time, accuracy, uptime, or compliance rate.
          </li>
          <li>
            Include relevant volunteer work, projects, internships, military
            experience, training, certifications, languages, and organizations.
          </li>
          <li>
            Remove outdated or unrelated content when it crowds out stronger
            evidence for the specific announcement.
          </li>
        </ul>
      </Section>

      <Section title="Federal Agency Career & Job Sites" open>
        <p>
          USAJOBS is the central portal, but agency sites help you understand
          missions, hiring paths, internships, occupations, and component-level roles.
          The links below are intentionally styled as links so they are easy to scan.
        </p>
        <ul
          style={{
            ...listStyle,
            columns: '2 280px',
            listStylePosition: 'outside',
          }}
        >
          {agencyLinks.map(([label, href]) => (
            <li key={href} style={{ breakInside: 'avoid', marginBottom: '0.45rem' }}>
              <ExternalLink href={href}>{label}</ExternalLink>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Students, Recent Graduates & Pathways">
        <ul style={listStyle}>
          <li>
            Search USAJOBS for Students and Recent Graduates hiring paths,
            internships, Recent Graduates Program roles, and agency developmental programs.
          </li>
          <li>
            Use coursework, labs, capstones, campus jobs, volunteer work, and
            student leadership as evidence when they match the announcement.
          </li>
          <li>
            If a job uses education to qualify, confirm degree level, coursework,
            accreditation, transcript requirements, and graduation timing.
          </li>
          <li>
            Save a copy of each announcement you apply to. Announcements can close,
            and you may need the text later for interview preparation.
          </li>
        </ul>
      </Section>

      <Section title="Veterans, Military Spouses & Transition Strategy">
        <ul style={listStyle}>
          <li>
            Translate military duties into public-service impact: mission need,
            your action, measurable result, and the transferable federal skill.
          </li>
          <li>
            Keep clearance references general. Never include classified,
            controlled, or sensitive operational details.
          </li>
          <li>
            Review Required Documents carefully for DD-214, SF-15, VA letter,
            military spouse documentation, or other proof required by the announcement.
          </li>
          <li>
            Explain leadership, logistics, security, training, compliance,
            maintenance, operations, cyber, and emergency response work in plain language.
          </li>
        </ul>
      </Section>

      <Section title="Law Enforcement, Criminal Justice & Homeland Security Roles">
        <p>
          Federal public safety careers include sworn and non-sworn paths.
          Students should look beyond job titles and map their skills to mission areas.
        </p>
        <ul style={listStyle}>
          <li>
            <strong>Investigations and analysis:</strong> investigative support,
            intelligence research, crime analysis, digital evidence, and case documentation.
          </li>
          <li>
            <strong>Compliance and inspection:</strong> customs, import compliance,
            food safety, tax, aviation safety, consumer safety, and regulatory enforcement.
          </li>
          <li>
            <strong>Emergency and security operations:</strong> dispatch, security
            administration, emergency management, continuity planning, and protective services.
          </li>
          <li>
            <strong>Courts and legal support:</strong> paralegal work, victim/witness
            services, records, court operations, and legal assistance.
          </li>
          <li>
            <strong>Technical and cyber roles:</strong> IT, digital forensics,
            data analysis, telecommunications, cybersecurity, and systems support.
          </li>
        </ul>
      </Section>

      <Section title="Interview Preparation for Federal Roles">
        <ul style={listStyle}>
          <li>
            Prepare five or six Job-Action-Result stories tied to the announcement:
            teamwork, conflict, technical problem solving, leadership, public service,
            and learning from a mistake.
          </li>
          <li>
            Be ready to discuss why the agency mission matters to you and how your
            education, projects, work, or service connect to that mission.
          </li>
          <li>
            Bring evidence: resume copy, unofficial transcript when relevant,
            writing sample if requested, portfolio links, and announcement notes.
          </li>
          <li>
            Expect longer timelines than many non-federal roles. Status updates
            may move from received to reviewed, referred, selected, or not selected.
          </li>
        </ul>
      </Section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--primary-color)', marginBottom: '0.75rem' }}>
          Video Resources
        </h2>
        <p style={{ color: 'var(--muted-color)', marginBottom: '1rem' }}>
          Short guided videos for federal hiring, organization, career strategy,
          and veterans transition.
        </p>
        <div
          style={{
            display: 'flex',
            gap: '1.25rem',
            overflowX: 'auto',
            paddingBottom: '0.75rem',
            scrollSnapType: 'x mandatory',
          }}
        >
          {videos.map((video) => (
            <div key={video.url} style={videoCard}>
              <video
                controls
                preload="metadata"
                style={{
                  width: '100%',
                  borderRadius: '6px',
                  marginBottom: '0.5rem',
                  backgroundColor: '#000',
                }}
              >
                <source src={video.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <h3 style={{ color: 'var(--primary-color)', marginBottom: '0.25rem' }}>
                {video.title}
              </h3>
              <p style={{ fontSize: '0.9rem' }}>{video.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section style={cardStyle}>
        <h2 style={{ color: 'var(--primary-color)', marginTop: 0 }}>
          Program Manual
        </h2>
        <p>
          Download the College Employment Assistance Program handout for a
          concise overview of the program structure, tools, and career pathways.
        </p>
        <ExternalLink
          href="https://myresumebuilder.blob.core.windows.net/ceapvideos/College Employment Assistance Handout.pdf"
          style={{
            display: 'inline-block',
            padding: '0.55rem 0.8rem',
            backgroundColor: 'var(--primary-color)',
            color: '#fff',
            borderRadius: '6px',
            textDecoration: 'none',
            fontWeight: 700,
          }}
        >
          Download Manual (PDF)
        </ExternalLink>
      </section>

    </div>
  );
};

export default InfoScreen;
